import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import AppShell from "./components/AppShell";
import { AuthenticationGuard } from "./components/AuthenticationGuard";
import Home from "./pages/Home";
import Sessions from "./pages/Sessions";
import SessionDetail from "./pages/SessionDetail";
import Upload from "./pages/Upload";
import Progress from "./pages/Progress";
import Rankings from "./pages/Rankings";
import AthleteGallery from "./pages/AthleteGallery";
import Roster from "./pages/Roster";
import RosterMember from "./pages/RosterMember";
import Explore from "./pages/Explore";
import TrendDetail from "./pages/TrendDetail";
import SportsLibrary from "./pages/SportsLibrary";
import SportDetail from "./pages/SportDetail";
import Reports from "./pages/Reports";
import Profile from "./pages/Profile";
import DevLiveHarness, { DevPoseClipHarness } from "./pages/DevLiveHarness";
import Settings from "./pages/Settings";
import BackgroundWarmups from "./BackgroundWarmups";
import Redeem from "./pages/Redeem";
import { signupReturnTo } from "./signupReturn";

// The /admin group is the one lazy boundary in the router: internal tooling has no
// business in the main bundle every customer downloads. Pages are added inside
// AdminArea, not here, so they inherit the split (and the ADMIN gate) for free.
const AdminArea = React.lazy(() => import("./pages/admin/AdminArea"));

/**
 * If the URL has `?signup=google|apple|1`, kick off the matching Auth0
 * sign-up flow before AuthenticationGuard renders its generic redirect.
 *
 * The marketing site at /homepage/ uses this contract: its social-login
 * buttons just navigate visitors to `/web/?signup=google` and let the
 * dashboard SDK (which already owns Auth0 state cookies + PKCE) take it
 * from there. Cleared on first render so the redirect can't double-fire.
 */
function SignupFromQueryGate({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const [signupHint] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("signup");
  });

  useEffect(() => {
    if (!signupHint) return;
    if (isLoading || isAuthenticated) return;

    const connection =
      signupHint === "google"
        ? "google-oauth2"
        : signupHint === "apple"
        ? "apple"
        : undefined;

    const authorizationParams: Record<string, string> = {
      screen_hint: "signup",
    };
    if (connection) authorizationParams.connection = connection;

    // Back to the page they came for (the golf pages send /upload?signup=1), not the dashboard.
    loginWithRedirect({ authorizationParams, appState: { returnTo: signupReturnTo(window.location.pathname, window.location.search) } });
  }, [signupHint, isLoading, isAuthenticated, loginWithRedirect]);

  if (signupHint && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Redirecting to sign-up…
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <SignupFromQueryGate>
      {/* the pose model and the coaching voice start downloading on whichever screen opens first */}
      <BackgroundWarmups />
      <Routes>
        {process.env.NODE_ENV === "development" && (
          <Route path="dev/live/:id" element={<DevLiveHarness />} />
        )}
        {/* DEV-ONLY, like the harness above: the upload page outside the login wall, so the
            drop → on-device analysis → technique → upload journey can be looked at in a browser.
            The upload itself still needs a signed-in session. */}
        {process.env.NODE_ENV === "development" && (
          <Route path="dev/upload" element={<div className="p-6"><Upload /></div>} />
        )}
        {process.env.NODE_ENV === "development" && (
          <Route path="dev/poseclip/:id" element={<DevPoseClipHarness />} />
        )}
        {/* Public: a gift card's recipient lands here before they have an account. */}
        <Route path="redeem" element={<Redeem />} />
        <Route element={<AuthenticationGuard component={AppShell} />}>
          <Route index element={<Home />} />
          <Route path="sessions"     element={<Sessions />} />
          <Route path="sessions/:id" element={<SessionDetail />} />
          {/* Optional feedback-video version override, e.g. /sessions/3631/79 → v79 */}
          <Route path="sessions/:id/:version" element={<SessionDetail />} />
          <Route path="upload"       element={<Upload />} />
          <Route path="rankings"     element={<Rankings />} />
          <Route path="athletes/:contactId" element={<AthleteGallery />} />
          <Route path="progress"     element={<Progress />} />
          <Route path="roster"       element={<Roster />} />
          <Route path="roster/:id"   element={<RosterMember />} />
          <Route path="explore"            element={<Navigate to="/explore/feed" replace />} />
          <Route path="explore/feed"       element={<Explore />} />
          <Route path="explore/trends"     element={<Explore />} />
          <Route path="explore/search"     element={<Explore />} />
          <Route path="explore/trend/:trendId" element={<TrendDetail />} />
          <Route path="sports"         element={<SportsLibrary />} />
          <Route path="sports/:activityId" element={<SportDetail />} />
          <Route path="reports"      element={<Reports />} />
          <Route path="profile"      element={<Profile />} />
          <Route path="settings"     element={<Settings />} />
          <Route
            path="admin/*"
            element={
              <React.Suspense
                fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}
              >
                <AdminArea />
              </React.Suspense>
            }
          />
        </Route>
      </Routes>
    </SignupFromQueryGate>
  );
}

export default App;
